<template>
  <div class="backdrop-overlay" :class="{'full-width': isFullWidth}"></div>

  <div class="image-overlay" :class="{'full-width': isFullWidth}">
    <div class="overlay-header">
      <div class="header-left">
        <h3>{{ image.filename }}</h3>
        <p class="date-display">
          {{
            image.metadata?.dateTaken
              ? formatDate(image.metadata.dateTaken)
              : ""
          }}
        </p>
      </div>

      <div class="header-right">
        <div class="dropdown-container" ref="dropdownRef">
          <img
            class="dropdown-button"
            src="@/assets/icons/dots-vertical.svg"
            alt="Actions"
            width="25"
            @click="toggleDropdown"
          />
          <div class="dropdown-content" v-if="dropdownOpen">
            <button @click="downloadImage">
              {{ $t("imageNavigation.download") }}
            </button>
            <button @click="shareImage">
              {{ $t("imageNavigation.share") }}
            </button>
          </div>
        </div>
        <div class="close-button" @click="handleClose">
          <img src="@/assets/icons/close-white.svg" alt="Close" />
        </div>
        <div class="collapse-button">
          <img
            v-show="!isFullWidth"
            src="@/assets/icons/collapse-button.svg"
            alt="Collapse"
            @click="toggleFullWidth"
          />
          <img
            v-show="isFullWidth"
            src="@/assets/icons/decollapse-button.svg"
            alt="Collapse"
            @click="toggleFullWidth"
          />
        </div>
      </div>
    </div>

    <div class="image-container">
      <button
        v-if="canAnnotate"
        class="annotate-button"
        :class="{active: isDrawMode}"
        @click="toggleDrawMode"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 2V14M2 8H14"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <span>{{ $t("imageNavigation.annotate") }}</span>
      </button>

      <div v-if="isFullWidth" class="side-navigation-button left">
        <ChevronLeftIcon
          @click="previousImage"
          @mouseover="hoveredNavButton = 'left'"
          @mouseleave="hoveredNavButton = null"
          :class="{disabled: previousImageDisabled}"
          :color="
            hoveredNavButton === 'left' && !previousImageDisabled
              ? theme.gree300
              : 'white'
          "
        />
      </div>

      <div
        class="zoom-container"
        ref="zoomContainerRef"
        @wheel="handleWheel"
        @mousedown="handleContainerMouseDown"
        @mousemove="pan"
        @mouseup="stopPan"
        @mouseleave="stopPan"
        :style="{
          cursor: isDrawMode
            ? 'crosshair'
            : isPanning
              ? 'grabbing'
              : scale > 1
                ? 'grab'
                : 'default',
        }"
      >
        <div v-if="isLoading" class="loading-container">
          <q-spinner :color="theme.gree300" size="60px" />
          <p>
            {{ $t("imageNavigation.loading") }}
          </p>
        </div>
        <img
          v-show="initialImageLoaded && !highResImageLoaded && !isLoading"
          :src="image.compressedUrl"
          :alt="image.filename"
          id="image-fullscreen"
          ref="compressedImgRef"
          crossorigin="anonymous"
          class="image"
          @load="onInitialImageLoaded"
          :style="{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }"
        />
        <img
          v-show="highResImageLoaded && !isLoading"
          :src="highResImage"
          :alt="image.filename"
          id="image-fullscreen-hd"
          ref="highResImgRef"
          crossorigin="anonymous"
          class="image"
          @load="onHighResImageLoaded"
          :style="{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }"
        />

        <!-- Saved annotations -->
        <svg
          v-if="annotations.length"
          class="draw-overlay"
          style="pointer-events: none"
        >
          <template v-for="(ann, i) in annotations" :key="i">
            <rect
              v-if="!ann.isSaved || activeAnnotationIndex === i"
              :x="ann.imgX * scale + position.x"
              :y="ann.imgY * scale + position.y"
              :width="ann.imgWidth * scale"
              :height="ann.imgHeight * scale"
              class="draw-rect clickable-rect"
              style="pointer-events: all"
              @click.stop="activeAnnotationIndex = i"
            />
            <g
              v-if="ann.isSaved && activeAnnotationIndex !== i"
              :transform="`translate(${ann.imgX * scale + position.x + (ann.imgWidth * scale) / 2}, ${ann.imgY * scale + position.y + (ann.imgHeight * scale) / 2})`"
              class="annotation-tag"
              style="pointer-events: all"
              @click.stop="activeAnnotationIndex = i"
            >
              <g transform="translate(-24, -24)">
                <path
                  d="M36.7992 15.9992C36.7992 23.988 27.9368 32.308 24.9608 34.8776C24.6836 35.0861 24.3461 35.1988 23.9992 35.1988C23.6523 35.1988 23.3149 35.0861 23.0376 34.8776C20.0616 32.308 11.1992 23.988 11.1992 15.9992C11.1992 12.6044 12.5478 9.34872 14.9483 6.94825C17.3487 4.54779 20.6044 3.19922 23.9992 3.19922C27.394 3.19922 30.6497 4.54779 33.0502 6.94825C35.4507 9.34872 36.7992 12.6044 36.7992 15.9992Z"
                  fill="white"
                  stroke="#FA6930"
                  stroke-width="2.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M22.3984 11.1992H25.5984V20.7992H22.3984V11.1992ZM22.3984 23.9992H25.5984V27.1992H22.3984V23.9992Z"
                  fill="#FA6930"
                />
                <circle cx="24.2" cy="41.2" r="3.2" fill="white" />
              </g>
            </g>
          </template>
        </svg>

        <!-- Draw overlay -->
        <svg v-if="isDrawMode" class="draw-overlay">
          <rect
            v-if="drawRect"
            :x="drawRect.x"
            :y="drawRect.y"
            :width="drawRect.width"
            :height="drawRect.height"
            class="draw-rect"
          />
        </svg>

        <!-- Highlight point -->
        <!-- <img
          v-if="
            image &&
            (fullscreenCoordinates || highResolutionCoordinates) &&
            !isLoading
          "
          src="@/assets/icons/fullscreenpoint.png"
          alt="highlight point"
          class="highlight-point"
          :style="highlightPointStyle"
        /> -->
      </div>

      <div v-if="isFullWidth" class="side-navigation-button right">
        <ChevronRightIcon
          @click="nextImage"
          @mouseover="hoveredNavButton = 'right'"
          @mouseleave="hoveredNavButton = null"
          :class="{disabled: nextImageDisabled}"
          :color="
            hoveredNavButton === 'right' && !nextImageDisabled
              ? theme.gree300
              : 'white'
          "
        />
      </div>
    </div>

    <div class="overlay-footer">
      <div class="navigation-controls" v-show="!isFullWidth">
        <div class="navigation-button q-mr-sm">
          <ChevronLeftIcon
            @click="previousImage"
            @mouseover="hoveredNavButton = 'footer-left'"
            @mouseleave="hoveredNavButton = null"
            :class="{disabled: previousImageDisabled}"
            :color="
              hoveredNavButton === 'footer-left' && !previousImageDisabled
                ? theme.gree300
                : 'white'
            "
          />
        </div>
        <div class="navigation-button">
          <ChevronRightIcon
            @click="nextImage"
            @mouseover="hoveredNavButton = 'footer-right'"
            @mouseleave="hoveredNavButton = null"
            :class="{disabled: nextImageDisabled}"
            :color="
              hoveredNavButton === 'footer-right' && !nextImageDisabled
                ? theme.gree300
                : 'white'
            "
          />
        </div>
      </div>

      <div class="zoom-controls">
        <div class="zoom-controls-buttons">
          <div
            class="zoom-controls-button"
            @click="zoomIn()"
            @mouseover="hoveredButton = 1"
            @mouseleave="hoveredButton = null"
            :class="{disabled: isZoomInDisabled}"
          >
            <ZoomInIcon
              :color="
                hoveredButton === 1 && !isZoomInDisabled
                  ? theme.gree300
                  : 'white'
              "
            />
            <p>{{ $t("imageNavigation.zoomIn") }}</p>
          </div>
          <div
            class="zoom-controls-button"
            @click="zoomOut()"
            @mouseover="hoveredButton = 2"
            @mouseleave="hoveredButton = null"
            :class="{disabled: isZoomOutDisabled}"
          >
            <ZoomOutIcon
              :color="
                hoveredButton === 2 && !isZoomOutDisabled
                  ? theme.gree300
                  : 'white'
              "
            />
            <p>{{ $t("imageNavigation.zoomOut") }}</p>
          </div>
          <div
            class="zoom-controls-button reset-button"
            @click="resetZoom()"
            @mouseover="hoveredButton = 3"
            @mouseleave="hoveredButton = null"
            :class="{disabled: isResetDisabled}"
          >
            <p>{{ $t("imageNavigation.resetView") }}</p>
          </div>
        </div>
      </div>
    </div>
    <Share
      v-if="share"
      class="share-popup"
      @closeShare="closeShare"
      :image="image"
    />

    <AnnotationPanel
      v-if="activeAnnotation"
      :annotation="activeAnnotation"
      :annotationIndex="activeAnnotationIndex"
      class="annotation-panel-overlay"
      @close="activeAnnotationIndex = null"
      @delete="handleAnnotationDelete"
      @save="handleAnnotationSave"
    />
  </div>
</template>

<script setup>
import {
  ref,
  nextTick,
  reactive,
  watch,
  computed,
  onMounted,
  onUnmounted,
} from "vue";
import {QSpinner} from "quasar";
import ZoomOutIcon from "@/components/icons/ZoomOutIcon.vue";
import ZoomInIcon from "@/components/icons/ZoomInIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import Share from "@/components/Gallery/Share.vue";
import AnnotationPanel from "@/components/Gallery/AnnotationPanel.vue";
import {useRoute} from "vue-router";
import {MIN_SCALE, MAX_SCALE, ZOOM_SPEED} from "@/constants";
import {formatDate} from "@/utils/date-utils";
import {useTheme} from "@/composables/useTheme.js";
import annotationsApi from "@/api/endpoints/annotations.js";
import {isStaging} from "@/utils/env.js";

const {theme} = useTheme();
const route = useRoute();

const props = defineProps({
  image: {
    type: Object,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  imagesCount: {
    type: Number,
    default: 0,
  },
  selectedImageIndex: {
    type: Number,
    default: 0,
  },
  calculateOffsetForFullScreen: {
    type: Function,
    default: () => {},
  },
  originalImageWidth: {
    type: Number,
    required: true,
  },
  originalImageHeight: {
    type: Number,
    required: true,
  },
  initialAnnotations: {
    type: Array,
    default: () => [],
  },
  addAnnotationMarkerToModel: {
    type: Function,
    default: null,
  },
  highlightAnnotationId: {
    type: [Number, String],
    default: null,
  },
  isEmployee: {
    type: Boolean,
    default: false,
  },
  currentUserId: {
    type: Number,
    default: null,
  },
});

const ANNOTATION_ALLOWED_CUSTOMER_IDS = [];
const canAnnotate = computed(
  () =>
    isStaging &&
    (props.isEmployee ||
      ANNOTATION_ALLOWED_CUSTOMER_IDS.includes(props.currentUserId)),
);

const emit = defineEmits([
  "close",
  "nextImage",
  "previousImage",
  "annotationCreated",
]);
const dropdownOpen = ref(false);
const dropdownRef = ref(null);
const isFullWidth = ref(false);
const share = ref(false);
const zoomContainerRef = ref(null);
const compressedImgRef = ref(null);
const highResImgRef = ref(null);
const resizeObserver = ref(null);
const highResImageLoaded = ref(false);
const highResImageLoading = ref(false);
const highResImage = ref("");
const scale = ref(1);
const position = reactive({x: 0, y: 0});
const isPanning = ref(false);
const lastPosition = reactive({x: 0, y: 0});
const hoveredButton = ref(null);
const hoveredNavButton = ref(null);
const initialImageLoaded = ref(false);
const fullscreenCoordinates = ref(null);
const highResolutionCoordinates = ref(null);

// Draw mode
const isDrawMode = ref(false);
const isDrawing = ref(false);
const drawStart = reactive({x: 0, y: 0});
const drawRect = ref(null);
const annotations = ref([]);
const activeAnnotationIndex = ref(null);
const activeAnnotation = computed(() =>
  activeAnnotationIndex.value !== null
    ? annotations.value[activeAnnotationIndex.value]
    : null,
);

const handleContainerMouseDown = (event) => {
  if (isDrawMode.value) {
    startDraw(event);
  } else {
    startPan(event);
  }
};

const handleAnnotationDelete = async () => {
  const index = activeAnnotationIndex.value;
  const annotation = annotations.value[index];
  try {
    await annotationsApi.deleteAnnotation(annotation.id);
  } catch (e) {
    console.error("Failed to delete annotation:", e);
  } finally {
    annotations.value.splice(index, 1);
    activeAnnotationIndex.value = null;
  }
};

const handleAnnotationSave = async (formData) => {
  const index = activeAnnotationIndex.value;
  const annotation = annotations.value[index];
  try {
    const response = await annotationsApi.saveAnnotation(
      route.params.projectId,
      props.image.pk,
      {
        ...formData,
        predictedType: annotation.type,
        predictedSeverityLevel: annotation.severityLevel,
        predictedDescription: annotation.description,
        imgX: annotation.imgX,
        imgY: annotation.imgY,
        imgWidth: annotation.imgWidth,
        imgHeight: annotation.imgHeight,
        normX: annotation.normX,
        normY: annotation.normY,
        imageIndex: props.selectedImageIndex,
      },
    );
    annotations.value[index] = {
      ...annotations.value[index],
      id: response.data.id,
      isSaved: true,
      savedDamageType: formData.damageType,
      savedSeverityLevel: formData.severityLevel,
      savedDescription: formData.description,
    };

    // Back-project annotation centre onto the 3-D model
    const ann = annotations.value[index];
    if (
      ann.normX != null &&
      ann.normY != null &&
      props.addAnnotationMarkerToModel
    ) {
      props.addAnnotationMarkerToModel(
        props.selectedImageIndex,
        ann.normX,
        ann.normY,
        ann,
      );
    }
  } catch (e) {
    console.error("Failed to save annotation:", e);
  } finally {
    activeAnnotationIndex.value = null;
  }
};

const handleClose = () => {
  // Project saved annotation centres back onto the 3-D model before closing
  if (props.addAnnotationMarkerToModel) {
    annotations.value
      .filter((a) => a.isSaved && a.normX != null && a.normY != null)
      .forEach((a) => {
        props.addAnnotationMarkerToModel(
          props.selectedImageIndex,
          a.normX,
          a.normY,
          a,
        );
      });
  }
  emit("close");
};

const toggleDrawMode = () => {
  isDrawMode.value = !isDrawMode.value;
  if (!isDrawMode.value) {
    drawRect.value = null;
    isDrawing.value = false;
  }
};

const startDraw = (event) => {
  const rect = zoomContainerRef.value.getBoundingClientRect();
  drawStart.x = event.clientX - rect.left;
  drawStart.y = event.clientY - rect.top;
  isDrawing.value = true;
  drawRect.value = {x: drawStart.x, y: drawStart.y, width: 0, height: 0};
  document.addEventListener("mousemove", onDrawMove);
  document.addEventListener("mouseup", onDrawEnd);
};

const onDrawMove = (event) => {
  if (!isDrawing.value || !zoomContainerRef.value) return;
  const rect = zoomContainerRef.value.getBoundingClientRect();
  const currentX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
  const currentY = Math.max(0, Math.min(event.clientY - rect.top, rect.height));
  drawRect.value = {
    x: Math.min(drawStart.x, currentX),
    y: Math.min(drawStart.y, currentY),
    width: Math.abs(currentX - drawStart.x),
    height: Math.abs(currentY - drawStart.y),
  };
};

const cropImageToBase64 = async (ann) => {
  const imgElement = highResImageLoaded.value
    ? highResImgRef.value
    : compressedImgRef.value;
  if (!imgElement || !zoomContainerRef.value) return null;

  const containerW = zoomContainerRef.value.clientWidth;
  const containerH = zoomContainerRef.value.clientHeight;
  const naturalW = imgElement.naturalWidth;
  const naturalH = imgElement.naturalHeight;

  const elemX = ann.imgX;
  const elemY = ann.imgY;
  const elemW = ann.imgWidth;
  const elemH = ann.imgHeight;

  // Account for object-fit: contain letterboxing
  const containerAspect = containerW / containerH;
  const imageAspect = naturalW / naturalH;
  let displayW, displayH, offsetX, offsetY;
  if (containerAspect > imageAspect) {
    displayH = containerH;
    displayW = displayH * imageAspect;
    offsetX = (containerW - displayW) / 2;
    offsetY = 0;
  } else {
    displayW = containerW;
    displayH = displayW / imageAspect;
    offsetX = 0;
    offsetY = (containerH - displayH) / 2;
  }

  // Scale to natural image pixels
  const toNatural = naturalW / displayW;
  const cropX = Math.max(0, Math.round((elemX - offsetX) * toNatural));
  const cropY = Math.max(0, Math.round((elemY - offsetY) * toNatural));
  const cropW = Math.min(Math.round(elemW * toNatural), naturalW - cropX);
  const cropH = Math.min(Math.round(elemH * toNatural), naturalH - cropY);

  if (cropW <= 0 || cropH <= 0) return null;

  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;
  canvas
    .getContext("2d")
    .drawImage(imgElement, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(
            new Error(
              "canvas.toBlob returned null — canvas may be tainted (CORS)",
            ),
          );
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      },
      "image/jpeg",
      0.9,
    ),
  );
};

/** Converts the centre of a screen-space annotation rect to normalised image
 *  coordinates (0–1), accounting for pan, zoom, and object-fit:contain offset. */
const getAnnotationCenterNorm = (ann) => {
  const imgElement = highResImageLoaded.value
    ? highResImgRef.value
    : compressedImgRef.value;
  if (!imgElement || !zoomContainerRef.value) return null;

  const containerW = zoomContainerRef.value.clientWidth;
  const containerH = zoomContainerRef.value.clientHeight;
  const naturalW = imgElement.naturalWidth;
  const naturalH = imgElement.naturalHeight;
  if (!naturalW || !naturalH) return null;

  // Undo pan/zoom to get coords in image-element space
  const elemCX = (ann.x + ann.width / 2 - position.x) / scale.value;
  const elemCY = (ann.y + ann.height / 2 - position.y) / scale.value;

  // Undo object-fit:contain letterboxing
  const containerAspect = containerW / containerH;
  const imageAspect = naturalW / naturalH;
  let displayW, displayH, offsetX, offsetY;
  if (containerAspect > imageAspect) {
    displayH = containerH;
    displayW = displayH * imageAspect;
    offsetX = (containerW - displayW) / 2;
    offsetY = 0;
  } else {
    displayW = containerW;
    displayH = displayW / imageAspect;
    offsetX = 0;
    offsetY = (containerH - displayH) / 2;
  }

  const toNatural = naturalW / displayW;
  const pixelX = (elemCX - offsetX) * toNatural;
  const pixelY = (elemCY - offsetY) * toNatural;

  return {
    normX: Math.max(0, Math.min(1, pixelX / naturalW)),
    normY: Math.max(0, Math.min(1, pixelY / naturalH)),
  };
};

const onDrawEnd = async () => {
  document.removeEventListener("mousemove", onDrawMove);
  document.removeEventListener("mouseup", onDrawEnd);
  if (!isDrawing.value) return;
  isDrawing.value = false;

  if (drawRect.value && drawRect.value.width > 5 && drawRect.value.height > 5) {
    const annotation = {
      x: drawRect.value.x,
      y: drawRect.value.y,
      width: drawRect.value.width,
      height: drawRect.value.height,
      imgX: (drawRect.value.x - position.x) / scale.value,
      imgY: (drawRect.value.y - position.y) / scale.value,
      imgWidth: drawRect.value.width / scale.value,
      imgHeight: drawRect.value.height / scale.value,
      ...getAnnotationCenterNorm(drawRect.value),
      isAnalyzing: true,
      croppedImage: null,
      type: null,
      description: null,
    };
    const index = annotations.value.push(annotation) - 1;
    activeAnnotationIndex.value = index;
    isDrawMode.value = false;
    drawRect.value = null;
    emit("annotationCreated", annotation);

    try {
      const imageBase64 = await cropImageToBase64(annotation);
      annotations.value[index] = {
        ...annotations.value[index],
        croppedImage: imageBase64,
      };

      const projectId = route.params.projectId;
      const response = await annotationsApi.analyzeAnnotation(
        projectId,
        imageBase64,
      );
      annotations.value[index] = {
        ...annotations.value[index],
        isAnalyzing: false,
        id: response.data.id,
        type: response.data.damage_type,
        severityLevel: response.data.severity ?? null,
        description: response.data.description,
      };
    } catch (e) {
      annotations.value[index] = {
        ...annotations.value[index],
        isAnalyzing: false,
        analysisError: true,
      };
      console.error("Error creating annotations", e);
    }
  } else {
    isDrawMode.value = false;
    drawRect.value = null;
  }
};

const previousImageDisabled = computed(() => props.selectedImageIndex === 0);
const nextImageDisabled = computed(() => {
  if (props.selectedImageIndex) {
    return props.selectedImageIndex === props.imagesCount - 1;
  }
  return false;
});

const previousImage = () => {
  if (props.selectedImageIndex === 0) return;

  initialImageLoaded.value = false;
  highResImageLoaded.value = false;
  emit("previousImage");
};

const nextImage = () => {
  if (props.selectedImageIndex === props.imagesCount - 1) return;

  initialImageLoaded.value = false;
  highResImageLoaded.value = false;
  emit("nextImage");
};

const toggleFullWidth = () => {
  isFullWidth.value = !isFullWidth.value;

  if (props.image.coordinatesOnImage) {
    setTimeout(() => {
      let imageElement = document.querySelector("#image-fullscreen");
      if (imageElement) {
        const coordinates = calculateFullscreenCoordinates(imageElement);
        if (coordinates) {
          fullscreenCoordinates.value = coordinates;
        }
      }

      imageElement = document.querySelector("#image-fullscreen-hd");
      if (imageElement) {
        const coordinates = calculateFullscreenCoordinates(imageElement);
        if (coordinates) {
          highResolutionCoordinates.value = coordinates;
        }
      }
    }, 300);
  }
};

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;

  if (dropdownOpen.value) {
    nextTick(() => {
      document.addEventListener("click", handleClickOutside);
    });
  }
};

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    dropdownOpen.value = false;
    document.removeEventListener("click", handleClickOutside);
  }
};

const downloadImage = () => {
  const imageUrl = props.image.downloadUrl;
  if (!imageUrl) {
    console.error("No download URL available for the image");
    return;
  }

  // Open the download URL in a new window/tab which will trigger the download
  window.open(imageUrl, "_blank");

  // Close the dropdown after initiating download
  dropdownOpen.value = false;
};

const shareImage = () => {
  share.value = true;
};

const closeShare = () => {
  share.value = false;
};

// Add proper scaling functions for fullscreen coordinates
const scalePointToFullscreen = function (imageElement, originalPoint) {
  const fullscreenWidth = imageElement.naturalWidth;
  const fullscreenHeight = imageElement.naturalHeight;

  const scaleToFullscreenX = fullscreenWidth / props.originalImageWidth;
  const scaleToFullscreenY = fullscreenHeight / props.originalImageHeight;

  return {
    x: originalPoint.x * scaleToFullscreenX,
    y: originalPoint.y * scaleToFullscreenY,
  };
};

const scaleFullscreenToDisplayed = function (imageElement, pointFullscreen) {
  const fullscreenWidth = imageElement.naturalWidth;
  const fullscreenHeight = imageElement.naturalHeight;

  const containerWidth = imageElement.clientWidth;
  const containerHeight = imageElement.clientHeight;

  const fullscreenAspect = fullscreenWidth / fullscreenHeight;
  const containerAspect = containerWidth / containerHeight;

  let displayedWidth, displayedHeight, offsetX, offsetY;

  if (containerAspect > fullscreenAspect) {
    displayedHeight = containerHeight;
    displayedWidth = displayedHeight * fullscreenAspect;
    offsetX = (containerWidth - displayedWidth) / 2;
    offsetY = 0;
  } else {
    displayedWidth = containerWidth;
    displayedHeight = displayedWidth / fullscreenAspect;
    offsetX = 0;
    offsetY = (containerHeight - displayedHeight) / 2;
  }

  const scaleX = displayedWidth / fullscreenWidth;
  const scaleY = displayedHeight / fullscreenHeight;

  const pointDisplayed = {
    x: pointFullscreen.x * scaleX + offsetX,
    y: pointFullscreen.y * scaleY + offsetY,
  };

  return pointDisplayed;
};

const calculateFullscreenCoordinates = (imageElement) => {
  if (!imageElement || !props.image.coordinatesOnImage) {
    return null;
  }

  const originalPoint = {
    x: props.image.coordinatesOnImage.x * props.originalImageWidth,
    y: props.image.coordinatesOnImage.y * props.originalImageHeight,
  };

  const fullscreenPoint = scalePointToFullscreen(imageElement, originalPoint);
  const displayedPoint = scaleFullscreenToDisplayed(
    imageElement,
    fullscreenPoint,
  );

  return displayedPoint;
};

const loadHighResImage = () => {
  if (props.image.downloadUrl) {
    highResImageLoading.value = true;
    highResImage.value = props.image.downloadUrl;
  }
};

const onHighResImageLoaded = () => {
  highResImageLoaded.value = true;
  highResImageLoading.value = false;

  if (props.image.coordinatesOnImage) {
    setTimeout(() => {
      let imageElement = document.querySelector("#image-fullscreen-hd");
      if (imageElement) {
        const coordinates = calculateFullscreenCoordinates(imageElement);
        if (coordinates) {
          highResolutionCoordinates.value = coordinates;
        }
      }
    }, 500);
  }
};

const onInitialImageLoaded = () => {
  initialImageLoaded.value = true;

  if (props.image.coordinatesOnImage) {
    setTimeout(() => {
      let imageElement = document.querySelector("#image-fullscreen");
      if (imageElement) {
        const coordinates = calculateFullscreenCoordinates(imageElement);
        if (coordinates) {
          fullscreenCoordinates.value = coordinates;
        }
      }
    }, 500);
  }
};

// Watch for image prop changes
watch(
  () => props.image,
  (newImage, oldImage) => {
    if (newImage.pk === oldImage?.pk) return;

    // Reset image loading states when image changes
    if (newImage) {
      initialImageLoaded.value = false;
      highResImageLoaded.value = false;
      highResImageLoading.value = false;
      highResImage.value = "";
      scale.value = 1;
      position.x = 0;
      position.y = 0;
      // Reset coordinates to prevent highlight point jumping
      fullscreenCoordinates.value = null;
      activeAnnotationIndex.value = null;
      highResolutionCoordinates.value = null;
      // Load saved annotations for this image
      annotations.value = (props.initialAnnotations || [])
        .filter((ann) => ann.image_id === newImage.pk)
        .map((ann) => ({
          imgX: ann.x,
          imgY: ann.y,
          imgWidth: ann.width,
          imgHeight: ann.height,
          normX: ann.norm_x,
          normY: ann.norm_y,
          isSaved: true,
          id: ann.id,
          savedDamageType: ann.damage_type,
          savedSeverityLevel: ann.severity_level,
          savedDescription: ann.description,
          type: ann.predicted_type,
          severityLevel: ann.predicted_severity_level ?? null,
          description: ann.predicted_description,
          croppedImage: null,
        }));

      // Open annotation panel if a specific annotation was requested
      if (props.highlightAnnotationId != null) {
        // eslint-disable-next-line eqeqeq
        let idx = annotations.value.findIndex(
          (a) => a.id == props.highlightAnnotationId,
        );

        if (idx === -1) {
          // image_id filter may have failed (e.g. pk missing). Search initialAnnotations
          // directly by id and inject the annotation into the local list.
          // eslint-disable-next-line eqeqeq
          const raw = (props.initialAnnotations || []).find(
            (a) => a.id == props.highlightAnnotationId,
          );
          if (raw) {
            annotations.value.push({
              imgX: raw.x,
              imgY: raw.y,
              imgWidth: raw.width,
              imgHeight: raw.height,
              isSaved: true,
              id: raw.id,
              normX: raw.norm_x,
              normY: raw.norm_y,
              savedDamageType: raw.damage_type,
              savedSeverityLevel: raw.severity_level,
              savedDescription: raw.description,
              type: raw.predicted_type,
              severityLevel: raw.predicted_severity_level ?? null,
              description: raw.predicted_description,
              croppedImage: null,
            });
            idx = annotations.value.length - 1;
          }
        }

        if (idx !== -1) activeAnnotationIndex.value = idx;
      }
    }
  },
  {immediate: true},
);

watch(
  () => props.highlightAnnotationId,
  (id) => {
    if (id == null) return;
    // eslint-disable-next-line eqeqeq
    const idx = annotations.value.findIndex((a) => a.id == id);
    if (idx !== -1) activeAnnotationIndex.value = idx;
  },
);

// Zoom functions
function handleWheel(event) {
  event.preventDefault();
  const rect = zoomContainerRef.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  const zoomAmount = event.deltaY > 0 ? -ZOOM_SPEED : ZOOM_SPEED;
  const newScale = Math.min(
    Math.max(scale.value + zoomAmount, MIN_SCALE),
    MAX_SCALE,
  );

  if (
    newScale > scale.value &&
    !highResImageLoaded.value &&
    !highResImageLoading.value
  ) {
    loadHighResImage();
  }

  if (newScale !== scale.value) {
    zoomToPoint(mouseX, mouseY, newScale);
  }
}

function zoomToPoint(mouseX, mouseY, newScale) {
  const factor = newScale / scale.value;

  position.x = mouseX - (mouseX - position.x) * factor;
  position.y = mouseY - (mouseY - position.y) * factor;

  scale.value = newScale;
  constrainPan();
}

function zoomIn() {
  if (scale.value < MAX_SCALE) {
    const rect = zoomContainerRef.value.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const newScale = scale.value + ZOOM_SPEED;
    zoomToPoint(centerX, centerY, newScale);

    if (!highResImageLoaded.value && !highResImageLoading.value) {
      loadHighResImage();
    }
  }
}

function zoomOut() {
  if (scale.value > MIN_SCALE) {
    const rect = zoomContainerRef.value.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const newScale = scale.value - ZOOM_SPEED;
    zoomToPoint(centerX, centerY, newScale);
  }
}

// Panning functions
function startPan(event) {
  if (scale.value > 1) {
    event.preventDefault();
    isPanning.value = true;
    lastPosition.x = event.clientX;
    lastPosition.y = event.clientY;
  }
}

function pan(event) {
  if (isPanning.value) {
    const deltaX = event.clientX - lastPosition.x;
    const deltaY = event.clientY - lastPosition.y;

    position.x += deltaX;
    position.y += deltaY;

    lastPosition.x = event.clientX;
    lastPosition.y = event.clientY;

    constrainPan();
  }
}

function stopPan() {
  isPanning.value = false;
}

function constrainPan() {
  if (!zoomContainerRef.value) return;

  const rect = zoomContainerRef.value.getBoundingClientRect();
  const scaledWidth = rect.width * scale.value;
  const scaledHeight = rect.height * scale.value;

  position.x = Math.min(0, Math.max(position.x, rect.width - scaledWidth));
  position.y = Math.min(0, Math.max(position.y, rect.height - scaledHeight));
}

function resetZoom() {
  scale.value = 1;
  position.x = 0;
  position.y = 0;
}

const isLoading = computed(() => {
  return (
    props.isLoading || (!initialImageLoaded.value && !highResImageLoaded.value)
  );
});

// Computed properties to track zoom disabled states
const isZoomOutDisabled = computed(() => {
  return scale.value <= MIN_SCALE;
});

const isZoomInDisabled = computed(() => {
  return scale.value >= MAX_SCALE;
});

const isResetDisabled = computed(() => {
  return scale.value <= MIN_SCALE; // Reset disabled when at minimum scale (same as zoom out disabled)
});

// Computed property for highlight point positioning
const highlightPointStyle = computed(() => {
  if (!props.image) {
    return {};
  }

  // Only use high-res coordinates if both high-res image is loaded AND coordinates are calculated
  // AND we're not currently in a zoom/pan operation to prevent jumping
  const useHighRes =
    highResImageLoaded.value &&
    highResolutionCoordinates.value &&
    !isPanning.value &&
    !highResImageLoading.value;

  const coordinates = useHighRes
    ? highResolutionCoordinates.value
    : fullscreenCoordinates.value;

  if (!coordinates) {
    return {};
  }

  // The coordinates from scalePointToFullScreen are already in pixels relative to the displayed image
  const pointX = coordinates.x;
  const pointY = coordinates.y;

  // Inverse scale: bigger when zoomed out, smaller when zoomed in
  const pointScale = 1 / Math.sqrt(scale.value);
  const baseSize = 50; // Base size in pixels

  // Apply the same transformation logic as the image
  return {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    left: `${pointX * scale.value + position.x}px`,
    top: `${pointY * scale.value + position.y}px`,
    pointerEvents: "none",
    zIndex: 10,
  };
});

const handleKeyDown = (event) => {
  if (event.key === "Escape" && isDrawMode.value) {
    isDrawMode.value = false;
    drawRect.value = null;
    isDrawing.value = false;
    document.removeEventListener("mousemove", onDrawMove);
    document.removeEventListener("mouseup", onDrawEnd);
  }
};

onMounted(() => {
  // Initialize resize observer to handle container size changes
  if (zoomContainerRef.value) {
    resizeObserver.value = new ResizeObserver(() => {
      // This will trigger recomputation of highlightPointStyle
      nextTick();
    });
    resizeObserver.value.observe(zoomContainerRef.value);
  }
  document.addEventListener("keydown", handleKeyDown);
});

onUnmounted(() => {
  // Clean up resize observer
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
  document.removeEventListener("keydown", handleKeyDown);
  document.removeEventListener("mousemove", onDrawMove);
  document.removeEventListener("mouseup", onDrawEnd);
});
</script>

<style lang="scss" scoped>
.backdrop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: calc(100vw - 300px);
  height: 100vh;
  background-color: var(--color-black);
  z-index: 999;
  transition: width 0.3s ease;

  &.full-width {
    width: 100vw;
  }
}

.image-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: calc(100vw - 300px);
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.3s ease;
  padding: 12px;
  background-color: var(--color-dark-bg-primary);

  &.full-width {
    width: 100vw;
  }
}

.overlay-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 1rem 0;
  background-color: var(--color-dark-bg-primary);
  color: white;

  .header-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }

    .date-display {
      margin: 0;
      font-size: 14px;
      color: var(--color-text-placeholder);
    }
  }

  .header-right {
    display: flex;
    position: relative;
    gap: 10px;
    height: 100%;

    .collapse-button {
      position: absolute;
      top: 60px;
      right: -29px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        width: 64px;
        height: 64px;
        z-index: 1;
        transition: transform 0.3s ease;
      }

      &:hover {
        opacity: 0.8;
        z-index: 1;
      }
    }

    .dropdown-container {
      position: relative;

      .dropdown-button {
        color: white;
        cursor: pointer;
      }

      .dropdown-content {
        position: absolute;
        right: 0;
        top: 100%;
        margin-top: 4px;
        background-color: var(--color-text-dark);
        min-width: 120px;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        z-index: 1001;

        button {
          display: block;
          width: 100%;
          text-align: left;
          padding: 8px 12px;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 14px;

          &:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
        }
      }
    }
    .close-button {
      cursor: pointer;
      padding: 4px 0;

      img {
        width: 16px;
        height: 16px;
      }
    }
  }
}

.image-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: var(--color-dark-bg-primary);
  transition: padding 0.3s ease;

  .zoom-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;

      p {
        color: white;
        margin: 0;
      }
    }

    .image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
  }
  .side-navigation-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(255, 255, 255, 0.1);
    border: none;
    padding: 2px;
    border-radius: 5px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(60, 60, 60, 0.8);
    }

    img {
      width: 32px;
      height: 32px;
      filter: brightness(0) invert(1);
    }

    &.left {
      left: 20px;
    }

    &.right {
      right: 20px;
    }
  }
}

.overlay-footer {
  padding: 16px;

  background-color: var(--color-dark-bg-primary);
  color: white;

  .footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
  }

  .image-info {
    width: 120px;
    p {
      margin: 5px 0;
      font-size: 14px;
      color: var(--color-text-placeholder);
    }
  }

  .zoom-controls {
    display: flex;
    align-items: center;
    justify-content: center;

    .zoom-controls-buttons {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-dark-bg-secondary);
      border-radius: 5px;

      .zoom-controls-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 8px 33px;
        border-right: 1px solid var(--color-border-divider);
        cursor: pointer;

        p {
          color: var(--color-white);
          font-size: 16px;
          font-weight: 600;
          user-select: none;
          transition: color 0.2s ease;
        }

        &:not(.disabled):hover p {
          color: var(--color-gree300);
        }

        svg {
          width: 16px;
          height: 16px;
          transition: fill 0.2s ease;
        }
      }

      .zoom-controls-button.reset-button {
        border: none;
      }
    }
  }

  .navigation-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    opacity: 1;
    max-height: 50px;
    overflow: hidden;
    transition:
      opacity 0.3s ease,
      max-height 0.3s ease;

    .navigation-button {
      display: flex;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.1);
      border: none;
      color: white;
      cursor: pointer;
      padding: 2px;
      border-radius: 5px;

      img {
        width: 32px;
        height: 32px;
        filter: brightness(0) invert(1);
      }
    }
  }
}

.image-overlay.full-width {
  .image-container {
    padding: 12px 60px;
  }
}

.image-overlay.full-width .navigation-controls {
  opacity: 0;
  max-height: 0;
  margin-bottom: 0;
}

.annotate-button {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--color-dark-bg-secondary);
  border: none;
  border-radius: 5px;
  color: var(--color-white);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover,
  &.active {
    color: var(--color-gree300);
  }
}

.draw-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;

  .draw-rect {
    fill: rgba(82, 196, 26, 0.1);
    stroke: var(--color-gree300);
    stroke-width: 2;
    stroke-dasharray: 6 3;
  }

  .clickable-rect {
    cursor: pointer;

    &:hover {
      fill: rgba(82, 196, 26, 0.25);
    }
  }

  .annotation-tag {
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.8;
    }
  }
}

.highlight-point {
  position: absolute;
  transform-origin: center;
  z-index: 5;
  pointer-events: none;
}
.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-container {
  position: relative;
}

.annotation-panel-overlay {
  height: 85%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 280px;
  z-index: 10;
}
</style>
